import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Form, Input, Modal, Select, Typography, message,
} from 'antd';

import { getAllCompanies, moveCompanyWhatsAppSender } from '../api';
import {
  AbtBotOption, Sender, apiError, formatSender, loadAbtBotOptions, unwrap,
} from './shared';

const { Paragraph } = Typography;

const NONE = '__none__';

interface CompanyOption {
  value: string;
  label: string;
}

interface Props {
  open: boolean;
  companyId: string;
  companyName?: string;
  sender: Sender;
  onClose: () => void;
  onMoved: () => void;
}

/**
 * Move the company's WhatsApp number to another company (ADR-037). The source
 * keeps its conversations read-only; the target gets the number fresh.
 */
const SenderMoveModal: React.FC<Props> = ({
  open, companyId, companyName, sender, onClose, onMoved,
}) => {
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [targetBots, setTargetBots] = useState<AbtBotOption[]>([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetId: string | undefined = Form.useWatch('targetCompanyId', form);
  const target = useMemo(() => companies.find((c) => c.value === targetId), [companies, targetId]);
  const current = companyName || 'this company';

  useEffect(() => {
    if (!open) return;
    setError(null);
    form.resetFields();
    setTargetBots([]);
    setCompaniesLoading(true);
    getAllCompanies()
      .then((res: any) => {
        const rows = unwrap(res);
        setCompanies((Array.isArray(rows) ? rows : [])
          .filter((c: any) => c && c.id && c.id !== companyId)
          .map((c: any) => ({ value: c.id, label: c.active === false ? `${c.name} (inactive)` : c.name })));
      })
      .catch(() => setCompanies([]))
      .finally(() => setCompaniesLoading(false));
  }, [open, companyId, form]);

  useEffect(() => {
    if (!open) return undefined;
    form.setFieldsValue({ abtBotId: NONE });
    setTargetBots([]);
    if (!targetId) return undefined;
    let cancelled = false;
    setBotsLoading(true);
    loadAbtBotOptions(targetId).then((opts) => {
      if (!cancelled) setTargetBots(opts);
    }).finally(() => {
      if (!cancelled) setBotsLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, targetId, form]);

  const handleMove = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setMoving(true);
    setError(null);
    try {
      await moveCompanyWhatsAppSender(companyId, sender.botmapId, {
        targetCompanyId: values.targetCompanyId,
        abtBotId: values.abtBotId && values.abtBotId !== NONE ? values.abtBotId : null,
        displayName: String(values.displayName || '').trim() || undefined,
      });
      message.success(`Moved to ${target ? target.label : 'the other company'}`);
      onMoved();
    } catch (err) {
      setError(apiError(err, 'Failed to move the WhatsApp number.'));
    } finally {
      setMoving(false);
    }
  };

  const targetName = target ? target.label.replace(/ \(inactive\)$/, '') : 'the new company';

  return (
    <Modal
      open={open}
      title={`Move ${formatSender(sender.sender)} to another company`}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button
          key="move"
          type="primary"
          loading={moving}
          disabled={!targetId}
          onClick={handleMove}
          data-testid="whatsapp-move-confirm"
        >
          Move number
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false} data-testid="whatsapp-move-modal">
        <Form.Item
          name="targetCompanyId"
          label="Company"
          rules={[{ required: true, message: 'Pick the company that gets this number' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Search companies"
            loading={companiesLoading}
            options={companies}
            data-testid="whatsapp-move-company"
          />
        </Form.Item>
        <Form.Item
          name="abtBotId"
          label="Apply-By-Text bot"
          initialValue={NONE}
          extra={targetId ? `A flow of ${targetName} that answers candidates.` : 'Pick a company first.'}
        >
          <Select
            showSearch
            optionFilterProp="label"
            disabled={!targetId}
            loading={botsLoading}
            options={[{ value: NONE, label: 'No bot (inbox only)' }, ...targetBots]}
          />
        </Form.Item>
        <Form.Item name="displayName" label="Display name">
          <Input
            maxLength={120}
            autoComplete="off"
            placeholder={`WhatsApp - ${targetId ? targetName : 'company'}`}
          />
        </Form.Item>
      </Form>
      <Paragraph type="secondary" style={{ fontSize: 13 }} data-testid="whatsapp-move-explainer">
        Conversations stay with {current} as read-only history. {targetName} starts with an empty WhatsApp inbox,
        and all {targetName} users are put on the number. Held and scheduled WhatsApp messages for {current} are
        cancelled.
      </Paragraph>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
};

export default SenderMoveModal;
